import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { BRAND, PHONES, buildWhatsAppLink } from "@/lib/constants";

const NAV = [
    { label: "Início", to: "/" },
    { label: "Sobre a PEG", to: "/sobre" },
    { label: "Aluguel de Veículos", to: "/aluguel-de-veiculos" },
    { label: "Rastreamento Veicular", to: "/rastreamento-veicular" },
    { label: "Guincho 24h", to: "/guincho-24-horas" },
    { label: "Contato", to: "/contato" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const headerCta = buildWhatsAppLink(
        PHONES.aluguel.raw,
        "Olá PEG Serviços! Gostaria de solicitar atendimento."
    );

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-[#f5f5f5]/95 backdrop-blur-md border-b border-black/10 shadow-sm"
                    : "bg-[#f5f5f5]"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20">
                    {/* Logo */}
                    <Link
                        to="/"
                        data-testid="header-logo-link"
                        className="flex items-center gap-3 group"
                    >
                        <img
                            src={BRAND.logo}
                            alt="PEG Serviços"
                            className="h-12 w-auto object-contain"
                            data-testid="header-logo-img"
                        />
                    </Link>

                    {/* Desktop nav */}
                    <nav className="hidden lg:flex items-center gap-8">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                data-testid={`nav-link-${item.to.replace(/\//g, "") || "home"}`}
                                className={({ isActive }) =>
                                    `text-sm font-semibold transition-colors relative group ${
                                        isActive
                                            ? "text-[#1a1a1a]"
                                            : "text-[#4a4a4a] hover:text-[#1a1a1a]"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        {item.label}
                                        <span
                                            className={`absolute left-0 -bottom-1.5 h-[3px] bg-[#fec200] transition-all ${
                                                isActive ? "w-full" : "w-0 group-hover:w-full"
                                            }`}
                                        />
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    {/* CTA */}
                    <div className="hidden lg:flex items-center">
                        <a
                            href={headerCta}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="header-cta-whatsapp"
                            className="inline-flex items-center gap-2 bg-[#1a1a1a] text-white font-bold px-6 py-3 hover:bg-[#fec200] hover:text-[#1a1a1a] transition-colors"
                        >
                            <MessageCircle className="w-4 h-4" />
                            Solicitar Atendimento
                        </a>
                    </div>

                    {/* Mobile burger */}
                    <button
                        onClick={() => setOpen((o) => !o)}
                        data-testid="mobile-menu-toggle"
                        className="lg:hidden p-2 text-[#1a1a1a]"
                        aria-label="Abrir menu"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    data-testid="mobile-menu"
                    className="lg:hidden bg-[#1a1a1a] text-white border-t border-[#333]"
                >
                    <div className="px-4 py-6 flex flex-col gap-1">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/"}
                                data-testid={`mobile-nav-${item.to.replace(/\//g, "") || "home"}`}
                                className={({ isActive }) =>
                                    `py-3 px-4 font-semibold border-l-4 ${
                                        isActive
                                            ? "border-[#fec200] bg-white/5"
                                            : "border-transparent hover:border-[#fec200] hover:bg-white/5"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                        <a
                            href={headerCta}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="mobile-cta-whatsapp"
                            className="mt-4 inline-flex items-center justify-center gap-2 bg-[#fec200] text-[#1a1a1a] font-bold px-6 py-4 hover:bg-white transition-colors"
                        >
                            <MessageCircle className="w-5 h-5" />
                            Solicitar Atendimento
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
